import type { ChangeLogEntry } from '~/models/ChangeLogModel'

export function getDifferences<T>(obj1: T, obj2: T): ChangeLogEntry[] {
  const changes: ChangeLogEntry[] = []

  function isTimestamp(value: any): boolean {
    return typeof value?.toMillis === 'function' && value?.seconds !== undefined
  }

  function format(value: any): string {
    if (value instanceof Date)
      return value.toISOString()
    if (isTimestamp(value))
      return new Date(value.toMillis()).toISOString()
    if (typeof value?.isEqual === 'function')
      return `[DocumentReference: ${value?.path || 'unknown'}]`
    if (value instanceof Map)
      return JSON.stringify(Object.fromEntries(value))
    if (typeof value === 'object' && value !== null)
      return JSON.stringify(value)
    return String(value)
  }

  function compare(val1: any, val2: any, path: string) {
    const val1IsUndef = typeof val1 === 'undefined'
    const val2IsUndef = typeof val2 === 'undefined'
    if (val1IsUndef && val2IsUndef)
      return
    if (val1IsUndef && !(val2 !== undefined))
      return
    if (val2IsUndef && !(val1 !== undefined))
      return

    if (Object.is(val1, val2) || (val1 == null && val2 == null))
      return

    const val1IsDateOrTimestamp = val1 instanceof Date || isTimestamp(val1)
    const val2IsDateOrTimestamp = val2 instanceof Date || isTimestamp(val2)

    if (val1IsDateOrTimestamp && val2IsDateOrTimestamp) {
      const t1 = val1 instanceof Date ? val1.getTime() : val1.toMillis()
      const t2 = val2 instanceof Date ? val2.getTime() : val2.toMillis()
      if (!Object.is(t1, t2)) {
        changes.push({
          fieldChanged: path,
          oldValue: format(val1),
          newValue: format(val2),
        })
      }
      return
    }

    if (
      typeof val1?.isEqual === 'function'
      && typeof val2?.isEqual === 'function'
    ) {
      if (!val1.isEqual(val2)) {
        changes.push({
          fieldChanged: path,
          oldValue: format(val1),
          newValue: format(val2),
        })
      }
      return
    }

    if (val1 instanceof Map && val2 instanceof Map) {
      const allKeys = new Set([...val1.keys(), ...val2.keys()])
      for (const key of allKeys) {
        const subPath = path ? `${path}.${String(key)}` : String(key)
        compare(val1.get(key), val2.get(key), subPath)
      }
      return
    }

    if (Array.isArray(val1) && Array.isArray(val2)) {
      const maxLength = Math.max(val1.length, val2.length)
      for (let i = 0; i < maxLength; i++) {
        const subPath = path ? `${path}[${i}]` : `[${i}]`
        compare(val1[i], val2[i], subPath)
      }
      return
    }

    if (
      typeof val1 === 'object'
      && typeof val2 === 'object'
      && val1 !== null
      && val2 !== null
      && !Array.isArray(val1)
      && !Array.isArray(val2)
    ) {
      const allKeys = new Set([...Object.keys(val1), ...Object.keys(val2)])
      for (const key of allKeys) {
        const subPath = path ? `${path}.${key}` : key
        compare(val1[key], val2[key], subPath)
      }
      return
    }

    if (!Object.is(val1, val2)) {
      changes.push({
        fieldChanged: path,
        oldValue: format(val1),
        newValue: format(val2),
      })
    }
  }

  compare(obj1, obj2, '')

  return changes
}

export function compareRouteGetProducts<T>(
  obj1: T,
  obj2: T,
): ChangeLogEntry[] {
  const arr1 = Array.isArray((obj1 as any)?.routeGetProducts)
    ? (obj1 as any).routeGetProducts
    : []
  const arr2 = Array.isArray((obj2 as any)?.routeGetProducts)
    ? (obj2 as any).routeGetProducts
    : []

  function format(item: any): string {
    if (!item || typeof item !== 'object')
      return String(item)
    const name = item.name ?? ''
    const up = typeof item.upAmount !== 'undefined' ? item.upAmount : ''
    const down = typeof item.downAmount !== 'undefined' ? item.downAmount : ''
    const isGet = typeof item.isGet !== 'undefined' ? item.isGet : ''
    return `${name}, Up: ${up}, Down: ${down}, ${isGet ? 'Get' : 'Back'}`
  }

  const changes: ChangeLogEntry[] = []

  if (arr2.length > arr1.length) {
    const arr1Formatted = arr1.map(format)
    const added = arr2.find((el: string) => !arr1Formatted.includes(format(el)))
    if (added) {
      changes.push({
        fieldChanged: 'routeGetProducts',
        oldValue: '',
        newValue: format(added),
      })
    }
    return changes
  }

  if (arr2.length < arr1.length) {
    const arr2Formatted = arr2.map(format)
    const removed = arr1.find((el: string) => !arr2Formatted.includes(format(el)))
    if (removed) {
      changes.push({
        fieldChanged: 'routeGetProducts',
        oldValue: format(removed),
        newValue: '',
      })
    }
    return changes
  }

  if (arr1.length === arr2.length && arr1.length > 0) {
    let idx = -1
    for (let i = 0; i < arr1.length; i++) {
      if (format(arr1[i]) !== format(arr2[i])) {
        idx = i
        break
      }
    }
    if (idx !== -1) {
      changes.push({
        fieldChanged: 'routeGetProducts',
        oldValue: format(arr1[idx]),
        newValue: format(arr2[idx]),
      })
    }
  }

  return changes
}
